/**
 * MediaPipe FaceLandmarker init + the frame loop.
 *
 * Owns everything that needs a camera, so `mouthfeatures.js` can stay testable.
 * Produces one MouthSample per video frame into a RingBuffer, timestamped with
 * `performance.now()` at the moment the frame was pulled — rule 1 of the
 * architecture section, and the reason Phase 2 can ask what the face was doing
 * 300 ms ago and get an answer worth having.
 *
 * The wasm runtime and the model are served from this origin
 * (see scripts/vendor-assets.mjs). The CDN paths in MediaPipe's own docs would
 * be blocked by the CSP in public/_headers.
 */

import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { config } from '../core/config.js';
import { MouthFeatures } from './mouthfeatures.js';

const WASM_PATH = '/mediapipe/wasm';
const MODEL_PATH = '/mediapipe/face_landmarker.task';

/**
 * Run a callback once per *new* video frame.
 *
 * `requestVideoFrameCallback` fires on actual frame presentation, so a 30 fps
 * camera under a 60 Hz rAF loop is not sampled twice. That matters beyond
 * wasted GPU: MediaPipe rejects a repeated timestamp, and a duplicated frame
 * would put two MouthSamples at different times on identical geometry, which
 * reads downstream as the face holding perfectly still.
 *
 * Falls back to rAF plus a currentTime check where rVFC is unavailable.
 *
 * @param {HTMLVideoElement} video
 * @param {() => void} onFrame
 * @returns {() => void} stop
 */
function everyFrame(video, onFrame) {
  let live = true;

  if (typeof video.requestVideoFrameCallback === 'function') {
    let handle = video.requestVideoFrameCallback(function tick() {
      if (!live) return;
      onFrame();
      handle = video.requestVideoFrameCallback(tick);
    });
    return () => {
      live = false;
      video.cancelVideoFrameCallback?.(handle);
    };
  }

  let lastTime = -1;
  let raf = requestAnimationFrame(function tick() {
    if (!live) return;
    if (video.currentTime !== lastTime) {
      lastTime = video.currentTime;
      onFrame();
    }
    raf = requestAnimationFrame(tick);
  });
  return () => {
    live = false;
    cancelAnimationFrame(raf);
  };
}

export class Landmarker {
  /**
   * @param {object} o
   * @param {HTMLVideoElement} o.video
   * @param {import('../core/ringbuffer.js').RingBuffer} o.buffer where MouthSamples land
   * @param {(state: string, detail?: string) => void} [o.onState]
   * @param {(sample: import('../core/types.js').MouthSample) => void} [o.onSample]
   *   called for every sample as it is produced. The recorder hangs off this
   *   rather than polling the buffer, so nothing is lost to a wrap or a stutter.
   */
  constructor({ video, buffer, onState, onSample }) {
    this.video = video;
    this.buffer = buffer;
    this.onState = onState ?? (() => {});
    this.onSample = onSample ?? (() => {});
    this.features = new MouthFeatures();

    /** 'idle' | 'starting' | 'running' | 'error' */
    this.state = 'idle';
    /** Frames with a face, and frames without, since start. */
    this.stats = { frames: 0, faces: 0, fps: 0, delegate: null };

    /** Most recent raw landmarks, for the overlay. @type {?object[]} */
    this.lastLandmarks = null;

    this._landmarker = null;
    this._stream = null;
    this._stopLoop = null;
    this._fpsWindow = [];
    /** MediaPipe requires strictly increasing timestamps. */
    this._lastTimestamp = -1;
    /** performance.now() of the last delivered frame. The liveness signal. */
    this._lastFrameAt = 0;
    this._recovering = false;
  }

  /** Open, whether or not frames are currently arriving. */
  get active() {
    return this.state === 'running' || this.state === 'stalled';
  }

  /**
   * Has the camera stopped delivering frames?
   *
   * Called from the render loop, because that is the only thing still running
   * when the frame callback has stopped. `requestVideoFrameCallback` gives no
   * indication that it has given up — it simply never fires again, and every
   * downstream reader goes on being handed the last landmarks it saw.
   *
   * The consequence is worse than a frozen overlay. Phase 6's vision gate asks
   * whether the face was moving; a face that is not being *sampled* is
   * indistinguishable from a face that is not moving, so a dead camera would
   * silently start vetoing every gesture as "the speaker was still".
   *
   * @param {number} now performance.now()
   * @returns {boolean} true while stalled
   */
  check(now = performance.now()) {
    if (!this.active) return false;

    const quiet = now - this._lastFrameAt;
    if (quiet <= config.vision.STALL_MS) {
      if (this.state === 'stalled') this._set('running', 'recovered');
      return false;
    }

    if (this.state !== 'stalled') {
      this._set('stalled', `no frames for ${Math.round(quiet)} ms`);
      this._recover();
    }
    return true;
  }

  /**
   * Try to get frames flowing again.
   *
   * On iOS the video element is paused when the page stops being visible, and
   * nothing resumes it on the way back. `play()` is usually the whole fix; a
   * track that has genuinely ended needs the camera reopening.
   */
  async _recover() {
    if (this._recovering) return;
    this._recovering = true;
    try {
      const track = this._stream?.getVideoTracks?.()[0];
      if (track && track.readyState === 'ended') {
        this.stop();
        await this.start();
      } else if (this.video.paused) {
        await this.video.play();
      }
    } catch (err) {
      // Stalled, not errored: the camera may come back, and marking it fatal
      // would hide it behind a "start camera" button mid-take.
      this._set('stalled', `could not restart: ${err?.message ?? err}`);
    } finally {
      this._recovering = false;
    }
  }

  _set(state, detail) {
    this.state = state;
    this.onState(state, detail);
  }

  /**
   * Create the landmarker, preferring the GPU delegate.
   *
   * GPU is several times faster but unavailable on some machines and inside some
   * remote desktop sessions. Falling back to CPU keeps the project usable there
   * rather than presenting as a dead camera; which one is in use is reported,
   * because it changes the frame rate enough to matter to Phase 5.
   */
  async _createLandmarker() {
    const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);
    const options = {
      baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'GPU' },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: false,
    };

    try {
      this._landmarker = await FaceLandmarker.createFromOptions(fileset, options);
      this.stats.delegate = 'GPU';
    } catch (err) {
      console.warn('GPU delegate unavailable, falling back to CPU:', err);
      options.baseOptions.delegate = 'CPU';
      this._landmarker = await FaceLandmarker.createFromOptions(fileset, options);
      this.stats.delegate = 'CPU';
    }
  }

  /** Open the camera and start producing samples. */
  async start() {
    if (this.state === 'running' || this.state === 'starting') return;
    this._set('starting', 'requesting camera');

    try {
      this._stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
        audio: false, // Phase 4 opens its own, with constraints that matter.
      });
      this.video.srcObject = this._stream;
      await this.video.play();

      this._set('starting', 'loading model');
      await this._createLandmarker();

      this.features.reset();
      this._lastTimestamp = -1;
      this._lastFrameAt = performance.now();
      this.stats.frames = 0;
      this.stats.faces = 0;

      this._stopLoop = everyFrame(this.video, () => this._onFrame());
      this._set('running', this.stats.delegate);
    } catch (err) {
      this.stop();
      this._set('error', err?.message ?? String(err));
      throw err;
    }
  }

  /** Release the camera and stop the loop. Safe to call from any state. */
  stop() {
    this._stopLoop?.();
    this._stopLoop = null;

    for (const track of this._stream?.getTracks() ?? []) track.stop();
    this._stream = null;
    this.video.srcObject = null;

    this._landmarker?.close?.();
    this._landmarker = null;

    if (this.state !== 'error') this._set('idle');
  }

  _onFrame() {
    const { video } = this;
    if (!this._landmarker || !video.videoWidth) return;

    // The one timestamp that matters. Taken here, before inference, because
    // that is when the frame existed — not when the landmarker finished with it.
    const t = performance.now();
    // Recorded before inference, and before any early return: a frame that
    // arrived and was dropped is still evidence the camera is alive.
    this._lastFrameAt = t;

    // Strictly increasing, which MediaPipe requires. performance.now() has
    // sub-millisecond resolution, but two frames inside the same tick would
    // otherwise throw.
    const stamp = t > this._lastTimestamp ? t : this._lastTimestamp + 0.001;
    this._lastTimestamp = stamp;

    let result;
    try {
      result = this._landmarker.detectForVideo(video, stamp);
    } catch (err) {
      console.warn('detectForVideo failed:', err);
      return;
    }

    this.stats.frames++;
    this._fpsWindow.push(t);
    while (this._fpsWindow.length && t - this._fpsWindow[0] > 1000) this._fpsWindow.shift();
    this.stats.fps = this._fpsWindow.length;

    const landmarks = result.faceLandmarks?.[0];
    const categories = result.faceBlendshapes?.[0]?.categories ?? [];
    // Kept for the overlay, which draws the handful of points the maths actually
    // reads. Seeing those sit on the right features is how you catch a wrong
    // index — the failure mode the spec warns about, since it never throws.
    this.lastLandmarks = landmarks ?? null;
    if (!landmarks) return;

    const aspect = video.videoWidth / video.videoHeight;
    const sample = this.features.update(landmarks, categories, t, aspect);
    if (!sample) return;

    this.stats.faces++;
    this.buffer.push(sample);
    this.onSample(sample);
  }

  /** Latest sample, or null. Convenience for the readout. */
  get latest() {
    return this.buffer.latest();
  }
}

/**
 * Whether this browser can run Phase 1 at all. Checked before offering the
 * button, so a missing capability reads as a missing capability rather than as
 * a camera that will not open.
 */
export function visionSupported() {
  return Boolean(navigator.mediaDevices?.getUserMedia && window.isSecureContext);
}

export default Landmarker;
